import { useState, useEffect, useCallback } from 'react';
import { Form, Spin, Alert, message, Empty, Card, Typography, Divider, Collapse, Input } from 'antd';
import { SaveOutlined, FolderOutlined, FileTextOutlined, EyeOutlined, EditOutlined, SendOutlined } from '@ant-design/icons';
import { Button } from '@bancobarigui/barihaus';
import { evaluateDisplayRule } from '@/utils/displayRuleEvaluator';
import { useChecklistDraft } from '@/features/laudo/hooks/useChecklistDraft';

const { Title, Text } = Typography;
const { Panel } = Collapse;
import { checklistService } from '@/services/checklists/checklist.service';
import { responseService } from '@/services/checklists/response.service';
import { docmanService } from '@/services/docman.service';
import { inspectionService } from '@/services/inspection.service';
import type { Checklist, ChecklistAnswer, SubmitChecklistResponseRequest, Question } from '@/services/types/checklist.types';
import { QuestionAnswerType } from '@/services/types/checklist.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { ChecklistData } from '@/features/laudo/types/laudo-wizard.types';
import { QuestionRenderer } from '@/components/DetailInspection/InspectionChecklist/QuestionRenderer';
import { AnswerDisplay } from '@/features/laudo/components/AnswerDisplay';
import type { QuestionResponse } from '@/features/laudo/types/checklist-response.types';

interface ChecklistStepProps {
  data: ChecklistData;
  inspection: InspectionRequest;
  onUpdate: (data: Partial<ChecklistData>) => void;
  onNext?: () => void;
  correctionMode?: boolean;
}

const answerTypeMap: Record<string, QuestionAnswerType> = {
  'ShortAnswer': QuestionAnswerType.ShortAnswer,
  'LongAnswer': QuestionAnswerType.LongAnswer,
  'RadioButton': QuestionAnswerType.RadioButton,
  'Checkbox': QuestionAnswerType.Checkbox,
  'YesNo': QuestionAnswerType.YesNo,
  'Date': QuestionAnswerType.Date,
  'NumericInteger': QuestionAnswerType.NumericInteger,
  'NumericDecimal': QuestionAnswerType.NumericDecimal,
  'Currency': QuestionAnswerType.Currency,
  'Table': QuestionAnswerType.Table,
  'Dropdown': QuestionAnswerType.Dropdown,
  'Scale': QuestionAnswerType.Scale,
  'Photo': QuestionAnswerType.Photo,
  'Signature': QuestionAnswerType.Signature,
  'File': QuestionAnswerType.File,
  'Rating': QuestionAnswerType.Rating,
  'Location': QuestionAnswerType.Location,
};

export function ChecklistStep({ data, inspection, onUpdate, onNext, correctionMode = false }: ChecklistStepProps) {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [checklistSaved, setChecklistSaved] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [pendingValues, setPendingValues] = useState<Record<string, any>>({});

  const checklistDraft = useChecklistDraft(
    checklist?.id,
    inspection.id,
    form,
    checklist,
  );

  const shouldShowJustification = useCallback((question: Question): boolean => {
    if (!question.hasDisplayRule || !question.displayRule) return false;
    const answer = formValues?.[question.id];
    if (answer === undefined || answer === null || answer === '') return false;
    return evaluateDisplayRule(question.displayRule, answer);
  }, [formValues]);

  useEffect(() => {
    loadChecklist();
  }, [inspection.items]);

  const loadChecklist = async () => {
    const inspectionTypeId = inspection.items?.[0]?.inspectionTypeId;

    if (!inspectionTypeId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await checklistService.getChecklistByInspectionType(inspectionTypeId);

      if (!response || !response.items || response.items.length === 0) {
        message.error('Nenhum checklist encontrado para este tipo de inspeção');
        setLoading(false);
        return;
      }

      const checklistData = response.items[0];
      setChecklist(checklistData);

      try {
        const responses = await responseService.getResponsesByInspectionId(
          checklistData.id,
          inspection.id
        );


        if (responses && responses.length > 0) {
          if (!correctionMode) {
            setChecklistSaved(true);
          }

          const formValues: Record<string, any> = {};

          responses.forEach((response: any) => {
            const question = findQuestionById(checklistData, response.questionId);
            if (!question) return;

            const answerType = answerTypeMap[question.answerType];
            const answer = response.answer || response;

            switch (answerType) {
              case QuestionAnswerType.ShortAnswer:
              case QuestionAnswerType.LongAnswer:
                formValues[response.questionId] = answer.textValue;
                break;
              case QuestionAnswerType.RadioButton:
              case QuestionAnswerType.YesNo:
              case QuestionAnswerType.Dropdown:
                formValues[response.questionId] = answer.selectedOption;
                break;
              case QuestionAnswerType.Checkbox:
                formValues[response.questionId] = answer.selectedOptions || [];
                break;
              case QuestionAnswerType.Date:
                formValues[response.questionId] = answer.dateValue;
                break;
              case QuestionAnswerType.NumericInteger:
              case QuestionAnswerType.NumericDecimal:
              case QuestionAnswerType.Currency:
              case QuestionAnswerType.Scale:
              case QuestionAnswerType.Rating:
                formValues[response.questionId] = answer.numericValue;
                break;
              case QuestionAnswerType.Table:
                formValues[response.questionId] = answer.tableData || [];
                break;
              case QuestionAnswerType.Photo:
                const photos = answer.photos || [];
                formValues[response.questionId] = {
                  fileList: photos.map((photo: any, idx: number) => {
                    // O documentId real do Docman vem em fileUrl no formato "docman/<id>".
                    // fileHash é hash do conteúdo, NÃO é o id do Docman — usar fileHash como
                    // documentId causa 404 no S3Image pra fotos não-uploadadas por este fluxo.
                    const docmanId = photo.fileUrl?.startsWith('docman/')
                      ? photo.fileUrl.replace('docman/', '')
                      : '';
                    return {
                      uid: docmanId || photo.fileHash || `photo-${idx}`,
                      name: photo.fileName || `foto-${idx + 1}.jpg`,
                      status: 'done' as const,
                      url: docmanId ? `/bari-api/docman/api/v1/documents/${docmanId}/download` : photo.fileUrl,
                      thumbUrl: docmanId ? `/bari-api/docman/api/v1/documents/${docmanId}/download` : photo.fileUrl,
                      documentId: docmanId,
                      isFromVideoConference: false,
                    };
                  }),
                };
                break;
              case QuestionAnswerType.File:
                formValues[response.questionId] = answer.files || [];
                break;
            }

            // Carregar justificativa/notes para campos condicionais
            if (answer.notes) {
              formValues[`${response.questionId}_justification`] = answer.notes;
            }
          });

          form.setFieldsValue(formValues);
          if (correctionMode) {
            setTimeout(() => {
              form.setFieldsValue(formValues);
            }, 1000);
          }
        } else {
          // Sem respostas submetidas - tentar carregar rascunho do IndexedDB
          checklistDraft.loadDraft();
        }
      } catch {
      }

    } catch {
      message.error('Erro ao carregar checklist');
    } finally {
      setLoading(false);
    }
  };

  const findQuestionById = (checklist: Checklist, questionId: string) => {
    for (const group of checklist.groups) {
      for (const section of group.sections) {
        const question = section.questions.find(q => q.id === questionId);
        if (question) return question;
      }
    }
    return null;
  };

  const uploadFile = async (file: any, questionTitle: string): Promise<{ documentId: string; fileUrl: string }> => {
    try {
      // Foto pré-existente: já tem documentId no Docman, não reupload.
      // fileUrl é gravado no formato canônico "docman/<id>" — não a URL pre-signed,
      // que expira e quebraria o reload do checklist depois.
      if (file.documentId) {
        return {
          documentId: file.documentId,
          fileUrl: `docman/${file.documentId}`,
        };
      }

      const originalFile = file.originFileObj || file;

      if (!originalFile || !(originalFile instanceof File)) {
        throw new Error(`Arquivo inválido para a pergunta "${questionTitle}"`);
      }

      const documentType = originalFile.type.startsWith('image/') ? 'FOTOS_IMOVEL' : 'OUTROS';

      const result = await docmanService.uploadFile(
        inspection.id,
        originalFile,
        documentType,
      );

      return {
        documentId: result.documentId,
        fileUrl: `docman/${result.documentId}`,
      };
    } catch (error) {
      throw new Error(`Erro ao fazer upload do arquivo para "${questionTitle}": ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  const handleReview = async () => {
    if (!checklist) {
      message.error('Checklist não carregado');
      return;
    }

    try {
      const values = await form.validateFields();

      // Validar campos obrigatórios
      for (const group of checklist.groups) {
        for (const section of group.sections) {
          for (const question of section.questions) {
            const value = values[question.id];
            if (question.isRequired && (value === undefined || value === null || value === '')) {
              throw new Error(`A pergunta "${question.title}" é obrigatória`);
            }
          }
        }
      }

      setPendingValues(values);
      setReviewMode(true);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  const handleBackToEdit = () => {
    setReviewMode(false);
  };

  const buildAnswerForDisplay = (question: any, value: any): QuestionResponse['answer'] => {
    const answerType = answerTypeMap[question.answerType];

    switch (answerType) {
      case QuestionAnswerType.ShortAnswer:
      case QuestionAnswerType.LongAnswer:
        return { textValue: value || '', isNotApplicable: false };
      case QuestionAnswerType.RadioButton:
      case QuestionAnswerType.YesNo:
      case QuestionAnswerType.Dropdown:
        return { selectedOption: value || '', isNotApplicable: false };
      case QuestionAnswerType.Checkbox:
        return { selectedOptions: Array.isArray(value) ? value : [value], isNotApplicable: false };
      case QuestionAnswerType.Date:
        return { dateValue: value || '', isNotApplicable: false };
      case QuestionAnswerType.NumericInteger:
      case QuestionAnswerType.NumericDecimal:
      case QuestionAnswerType.Scale:
      case QuestionAnswerType.Rating:
        return { numericValue: typeof value === 'number' ? value : parseFloat(value), isNotApplicable: false };
      case QuestionAnswerType.Currency:
        return { numericValue: typeof value === 'number' ? value : parseFloat(value), isNotApplicable: false };
      case QuestionAnswerType.Table:
        return { tableData: Array.isArray(value) ? value : [], isNotApplicable: false };
      case QuestionAnswerType.Photo:
        if (value && value.fileList && Array.isArray(value.fileList)) {
          return {
            photos: value.fileList.map((file: any) => ({
              fileName: file.name || file.fileName || '',
              fileUrl: file.documentId ? `docman/${file.documentId}` : (file.url || file.thumbUrl || ''),
              bucketPath: '',
              bucketName: '',
              caption: '',
              uploadedAt: new Date().toISOString(),
              fileSizeBytes: file.size || 0,
              fileHash: file.documentId || '',
            })),
            isNotApplicable: false,
          };
        }
        if (Array.isArray(value)) {
          return { photos: value, isNotApplicable: false };
        }
        return { photos: [], isNotApplicable: false };
      case QuestionAnswerType.File:
        if (value && value.fileList && Array.isArray(value.fileList)) {
          return {
            files: value.fileList.map((file: any) => ({
              fileName: file.name || file.fileName || '',
              fileUrl: file.documentId ? `docman/${file.documentId}` : (file.url || ''),
              fileType: file.type || '',
              bucketPath: '',
              bucketName: '',
              uploadedAt: new Date().toISOString(),
              fileSizeBytes: file.size || 0,
              fileHash: file.documentId || '',
            })),
            isNotApplicable: false,
          };
        }
        if (Array.isArray(value)) {
          return { files: value, isNotApplicable: false };
        }
        return { files: [], isNotApplicable: false };
      default:
        return { textValue: String(value || ''), isNotApplicable: false };
    }
  };

  const handleTransmit = async () => {
    if (!checklist) return;

    // Chamar handleSubmit com os valores pendentes
    await handleSubmit(pendingValues);
  };

  const handleSubmit = async (values: any) => {
    if (!checklist) {
      message.error('Checklist não carregado');
      return;
    }

    try {
      setSubmitting(true);

      const answers: ChecklistAnswer[] = [];

      for (const group of checklist.groups) {
        for (const section of group.sections) {
          for (const question of section.questions) {
            const value = values[question.id];

            if (value === undefined || value === null || value === '') {
              if (question.isRequired) {
                throw new Error(`A pergunta "${question.title}" é obrigatória`);
              }
              continue;
            }

            const answerType = answerTypeMap[question.answerType];

            if (answerType === undefined) {
              continue;
            }

            const answer: ChecklistAnswer = {
              questionId: question.id,
              questionAnswerType: answerType,
              isNotApplicable: false,
            };

            switch (answerType) {
              case QuestionAnswerType.ShortAnswer:
              case QuestionAnswerType.LongAnswer:
                answer.textValue = value;
                break;
              case QuestionAnswerType.RadioButton:
              case QuestionAnswerType.YesNo:
              case QuestionAnswerType.Dropdown:
                answer.selectedOption = value;
                break;
              case QuestionAnswerType.Checkbox:
                answer.selectedOptions = Array.isArray(value) ? value : [value];
                break;
              case QuestionAnswerType.Date:
                answer.dateValue = value;
                break;
              case QuestionAnswerType.NumericInteger:
              case QuestionAnswerType.NumericDecimal:
              case QuestionAnswerType.Currency:
              case QuestionAnswerType.Scale:
              case QuestionAnswerType.Rating:
                answer.numericValue = typeof value === 'number' ? value : parseFloat(value);
                break;
              case QuestionAnswerType.Table:
                if (Array.isArray(value)) {
                  answer.tableData = value;
                } else {
                  answer.tableData = [];
                }
                break;
              case QuestionAnswerType.Photo:
                if (value && value.fileList && Array.isArray(value.fileList)) {
                  const photoUploads = value.fileList.map(async (file: any) => {
                    const uploadResult = await uploadFile(file, question.title);
                    return {
                      fileName: file.name || file.fileName,
                      fileUrl: uploadResult.fileUrl,
                      bucketPath: '',
                      bucketName: '',
                      caption: '',
                      uploadedAt: new Date().toISOString(),
                      fileSizeBytes: file.size || 0,
                      imageWidth: file.imageWidth || 0,
                      imageHeight: file.imageHeight || 0,
                      fileHash: uploadResult.documentId,
                    };
                  });
                  answer.photos = await Promise.all(photoUploads);
                } else if (Array.isArray(value)) {
                  answer.photos = value;
                } else {
                  answer.photos = [];
                }
                break;
              case QuestionAnswerType.File:
                if (value && value.fileList && Array.isArray(value.fileList)) {
                  const fileUploads = value.fileList.map(async (file: any) => {
                    const uploadResult = await uploadFile(file, question.title);
                    return {
                      fileName: file.name || file.fileName,
                      fileUrl: uploadResult.fileUrl,
                      fileType: file.type || '',
                      bucketPath: '',
                      bucketName: '',
                      description: '',
                      uploadedAt: new Date().toISOString(),
                      fileSizeBytes: file.size || 0,
                      fileHash: uploadResult.documentId,
                    };
                  });
                  answer.files = await Promise.all(fileUploads);
                } else if (Array.isArray(value)) {
                  answer.files = value;
                } else {
                  answer.files = [];
                }
                break;
              default:
                answer.textValue = value;
            }

            const justificationKey = `${question.id}_justification`;
            const justificationValue = values[justificationKey];
            if (question.hasDisplayRule && question.displayRule) {
              const shouldInclude = evaluateDisplayRule(question.displayRule, value);
              if (shouldInclude && justificationValue) {
                answer.notes = justificationValue;
              }
            } else if (justificationValue) {
              answer.notes = justificationValue;
            }

            answers.push(answer);
          }
        }
      }

      const payload: SubmitChecklistResponseRequest = {
        checklistId: checklist.id,
        inspectionId: inspection.id,
        inspectionItemId: inspection.items?.[0]?.id,
        answers,
      };

      await responseService.submitResponse(checklist.id, payload);

      // NOVO FLUXO: Muda o status para AguardandoRevisaoPrestadora (13)
      // Inspetor só faz checklist, Prestadora/Banco geram o laudo
      const inspectionItemId = inspection.items?.[0]?.id;
      if (inspectionItemId) {
        try {
          await inspectionService.completeChecklist(inspectionItemId);
        } catch {
          // Não bloqueia o fluxo se falhar a mudança de status
        }
      }

      onUpdate({
        checklistId: checklist.id,
        completed: true,
        checklistPreenchido: true,
      });

      message.success('Checklist transmitido com sucesso!');
      setChecklistSaved(true);

      // Limpar rascunho apos transmissao bem-sucedida
      checklistDraft.clearDraft().catch(() => {});

      setTimeout(() => {
        onNext?.();
      }, 1500);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Erro ao salvar checklist');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Carregando checklist..." />
      </div>
    );
  }

  if (!checklist) {
    return (
      <Empty description="Nenhum checklist encontrado para este tipo de inspeção" />
    );
  }

  if (checklistSaved) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert
          message="Checklist Transmitido com Sucesso!"
          description="Todas as respostas do checklist foram salvas e enviadas para a prestadora. Você pode avançar para a próxima etapa."
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <p style={{ fontSize: 16, color: '#666', marginTop: 24 }}>
          Clique em "Próximo" para continuar
        </p>
      </div>
    );
  }

  // Encontrar primeira pergunta de foto
  let firstPhotoQuestionId: string | null = null;
  for (const group of checklist.groups) {
    for (const section of group.sections) {
      for (const question of section.questions) {
        if (answerTypeMap[question.answerType] === QuestionAnswerType.Photo) {
          firstPhotoQuestionId = question.id;
          break;
        }
      }
      if (firstPhotoQuestionId) break;
    }
    if (firstPhotoQuestionId) break;
  }

  // Gerar chaves padrão para abrir todos os grupos
  const defaultActiveKeys = checklist.groups.map(g => g.id);

  // Tela de conferência (review mode)
  if (reviewMode) {
    return (
      <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
        <Alert
          message="Conferência do Checklist"
          description="Revise todas as respostas antes de transmitir para a prestadora. Se precisar alterar alguma resposta, clique em Editar."
          type="warning"
          showIcon
          icon={<EyeOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Collapse
          defaultActiveKey={defaultActiveKeys}
          expandIconPosition="start"
          style={{ background: '#fff' }}
        >
          {checklist.groups.map((group) => (
            <Panel
              key={group.id}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FolderOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
                    {group.name}
                  </span>
                  <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>
                    {group.sections.length} {group.sections.length === 1 ? 'seção' : 'seções'}
                  </Text>
                </div>
              }
              style={{
                marginBottom: 16,
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                overflow: 'hidden',
              }}
            >
              {group.sections.map((section, sectionIndex) => (
                <Card
                  key={section.id}
                  size="small"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FileTextOutlined style={{ color: '#52c41a' }} />
                      <span style={{ fontWeight: 500, color: '#434343' }}>
                        {section.name}
                      </span>
                      <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                        {section.questions.length} {section.questions.length === 1 ? 'pergunta' : 'perguntas'}
                      </Text>
                    </div>
                  }
                  style={{
                    marginBottom: sectionIndex < group.sections.length - 1 ? 16 : 0,
                    borderRadius: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  }}
                  headStyle={{
                    background: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                  }}
                  bodyStyle={{
                    padding: '16px 20px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {section.questions.map((question) => {
                      const value = pendingValues[question.id];
                      if (value === undefined || value === null || value === '') return null;

                      const questionResponse: QuestionResponse = {
                        id: question.id,
                        checklistId: checklist.id,
                        questionId: question.id,
                        inspectionId: inspection.id,
                        respondedBy: '',
                        respondedAt: new Date().toISOString(),
                        question: {
                          id: question.id,
                          title: question.title,
                          answerType: question.answerType,
                        },
                        answer: buildAnswerForDisplay(question, value),
                      };

                      return (
                        <div
                          key={question.id}
                          style={{
                            padding: '12px 16px',
                            background: '#fafafa',
                            borderRadius: 6,
                            border: '1px solid #f0f0f0',
                          }}
                        >
                          <AnswerDisplay response={questionResponse} />
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </Panel>
          ))}
        </Collapse>

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, paddingTop: 8 }}>
          <Button
            buttonColor="secondary"
            icon={<EditOutlined />}
            size="large"
            style={{ minWidth: 180, height: 48, fontSize: 16 }}
            onClick={handleBackToEdit}
          >
            Editar
          </Button>
          <Button
            buttonColor="primary"
            icon={<SendOutlined />}
            loading={submitting}
            size="large"
            style={{ minWidth: 180, height: 48, fontSize: 16 }}
            onClick={handleTransmit}
          >
            Transmitir
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <Alert
        message="Checklist de Vistoria"
        description="Preencha todas as perguntas obrigatórias marcadas com asterisco (*)"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleReview}
      >
        <Collapse
          defaultActiveKey={defaultActiveKeys}
          expandIconPosition="start"
          style={{ background: '#fff' }}
        >
          {checklist.groups.map((group, groupIndex) => (
            <Panel
              key={group.id}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FolderOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
                    {group.name}
                  </span>
                  <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>
                    {group.sections.length} {group.sections.length === 1 ? 'seção' : 'seções'}
                  </Text>
                </div>
              }
              style={{
                marginBottom: 16,
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                overflow: 'hidden',
              }}
            >
              {group.sections.map((section, sectionIndex) => (
                <Card
                  key={section.id}
                  size="small"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FileTextOutlined style={{ color: '#52c41a' }} />
                      <span style={{ fontWeight: 500, color: '#434343' }}>
                        {section.name}
                      </span>
                      <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                        {section.questions.length} {section.questions.length === 1 ? 'pergunta' : 'perguntas'}
                      </Text>
                    </div>
                  }
                  style={{
                    marginBottom: sectionIndex < group.sections.length - 1 ? 16 : 0,
                    borderRadius: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  }}
                  headStyle={{
                    background: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                  }}
                  bodyStyle={{
                    padding: '16px 20px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {section.questions.map((question) => {
                      const showJustification = shouldShowJustification(question);
                      return (
                        <div
                          key={question.id}
                          style={{
                            padding: '16px',
                            background: '#fafafa',
                            borderRadius: 6,
                            border: '1px solid #f0f0f0',
                          }}
                        >
                          <QuestionRenderer
                            question={question}
                            inspectionItemId={inspection.items?.[0]?.id}
                            isFirstPhotoQuestion={question.id === firstPhotoQuestionId}
                          />
                          {showJustification && (
                            <div style={{ marginTop: 12 }}>
                              <label style={{ display: 'block', fontWeight: 500, marginBottom: 4, fontSize: 13 }}>
                                Justificativa *
                              </label>
                              <Form.Item
                                name={`${question.id}_justification`}
                                rules={[{ required: true, message: 'Justificativa obrigatória' }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input.TextArea
                                  rows={3}
                                  maxLength={500}
                                  placeholder="Informe a justificativa"
                                  showCount
                                />
                              </Form.Item>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </Panel>
          ))}
        </Collapse>

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, paddingTop: 8 }}>
          {checklistDraft.available && (
            <Button
              icon={<SaveOutlined />}
              onClick={(e) => {
                e.preventDefault();
                checklistDraft.saveDraft();
              }}
              loading={checklistDraft.saving}
              disabled={checklistDraft.saving}
              size="large"
              style={{ minWidth: 200, height: 48, fontSize: 16 }}
            >
              Salvar Rascunho
            </Button>
          )}
          <Button
            buttonColor="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={submitting}
            size="large"
            style={{ minWidth: 200, height: 48, fontSize: 16 }}
          >
            Enviar
          </Button>
        </div>
      </Form>
    </div>
  );
}
